import { AxiosError } from "axios";

import shipvistaAPIv2 from "./shipvistaAPIv2";

/**
 * v2 pre-login auth calls + a shared RFC 7807 problem+json error mapper.
 * NEXT-01 owns this file (login + MFA); NEXT-02 extends it (register/verify/reset)
 * and reuses `mapAuthError`.
 *
 * v2 contracts (ShipvistaAPI 02-identity §6, verified vs main 2026-07-13):
 *  - POST /v2/auth/login { email, password } → LoginTokens { accessToken, refreshToken, expiresAt }
 *    OR MfaChallenge { requiresMfa: true, mfaToken } (HTTP 200 for both arms).
 *  - POST /v2/auth/mfa/verify { mfaToken, code } → LoginTokens.
 *  - The mfaToken is a 5-minute CHALLENGE token, NOT a session token — never persist it
 *    beyond the challenge screen, never hand it to the SPA.
 *  - Errors are problem+json with real HTTP statuses (no `status:false`).
 */

/** Access TTL is 15 min in v2 — `expiresAt` is the ISO expiry of `accessToken`. */
export interface V2LoginTokens {
  accessToken: string;
  refreshToken: string;
  expiresAt: string;
}

export interface V2MfaChallenge {
  requiresMfa: true;
  mfaToken: string;
}

export type V2LoginResponse = V2LoginTokens | V2MfaChallenge;

export function isMfaChallenge(r: V2LoginResponse): r is V2MfaChallenge {
  return (r as V2MfaChallenge).requiresMfa === true;
}

export const loginV2 = (body: { email: string; password: string }) =>
  shipvistaAPIv2.post<V2LoginResponse>("/auth/login", body);

export const mfaVerifyV2 = (body: { mfaToken: string; code: string }) =>
  shipvistaAPIv2.post<V2LoginTokens>("/auth/mfa/verify", body);

// ── Registration + password reset (NEXT-02) ──────────────────────────────────
// 02-identity §6 (as-built ShipvistaAPI main 2026-07-13):
//  - POST /v2/auth/register → 201 { userId }. The contract is { email, password, company?,
//    isBusiness, referralTrackingId? } — it does NOT carry name or phone.
//  - POST /v2/auth/register/verify { email, code } → 200 LoginTokens (verify AUTO-ISSUES a
//    session → funnel through postLoginHandoff).
//  - POST /v2/auth/register/resend-code { email } → 204.
//  - POST /v2/auth/forgot-password { email } → 204 (always; emails a reset LINK, no code step).
//  - POST /v2/auth/reset-password { token, newPassword } → 204 (token comes from the emailed link).

export interface V2RegisterBody {
  email: string;
  password: string;
  company?: string;
  isBusiness: boolean;
  /** GUID from referral tracking — no v2 SPA source today (referral module unbuilt, see Findings). */
  referralTrackingId?: string;
}

export const registerV2 = (body: V2RegisterBody) =>
  shipvistaAPIv2.post<{ userId: string }>("/auth/register", body);

export const verifyRegisterV2 = (body: { email: string; code: string }) =>
  shipvistaAPIv2.post<V2LoginTokens>("/auth/register/verify", body);

export const resendCodeV2 = (body: { email: string }) =>
  shipvistaAPIv2.post<void>("/auth/register/resend-code", body);

export const forgotPasswordV2 = (body: { email: string }) =>
  shipvistaAPIv2.post<void>("/auth/forgot-password", body);

export const resetPasswordV2 = (body: { token: string; newPassword: string }) =>
  shipvistaAPIv2.post<void>("/auth/reset-password", body);

/** RFC 7807 shape returned on any 4xx/429 from the v2 auth endpoints. */
export interface ProblemDetails {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  code?: string;
}

/** Known auth error codes → user-facing copy. Unknown codes fall back to the server `detail`. */
const AUTH_MESSAGES: Record<string, string> = {
  "invalid-credentials": "Incorrect email or password.",
  "incorrect-current-password": "Your current password is incorrect.",
  "account-suspended":
    "Your account has been suspended. Please contact support.",
  "email-not-verified": "Please verify your email address before signing in.",
  "invalid-mfa-token":
    "Your verification session has expired. Please sign in again.",
  "invalid-code": "That code isn't right. Please try again.",
  "mfa-locked": "Too many attempts. Please wait a few minutes and try again.",
  "duplicate-email": "An account with this email already exists.",
  "invalid-token":
    "This link is invalid or has expired. Please request a new one.",
  "code-expired": "That code has expired. Please request a new one.",
  "validation-failed": "Please check the form and try again.",
};

/**
 * Normalise a thrown Axios error (v2 problem+json) into a single user-facing message.
 * Prefers a mapped known `code`, then the server `detail`/`title`, then a 429 hint,
 * then the supplied fallback. Also returns the raw `code` so callers can branch
 * (e.g. an expired mfaToken should bounce back to login).
 */
export function mapAuthError(
  error: unknown,
  fallback = "Something went wrong. Please try again."
): { message: string; code?: string; status?: number } {
  const ax = error as AxiosError<ProblemDetails>;
  const problem = ax?.response?.data;
  const status = ax?.response?.status;

  if (problem && typeof problem === "object") {
    const code = problem.code;
    if (code && AUTH_MESSAGES[code])
      return { message: AUTH_MESSAGES[code], code, status };
    if (problem.detail) return { message: problem.detail, code, status };
    if (problem.title) return { message: problem.title, code, status };
  }
  if (status === 429) {
    return { message: "Too many attempts. Please try again later.", status };
  }
  return { message: fallback, status };
}
